import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Plus, Users, FileEdit, Home, Truck, Settings, User, XCircle, CheckCircle, Briefcase, Edit } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const TEAM_ROLES = [
  { value: 'data_entry_team', label: 'Data Entry / QC & Approval Team', icon: FileEdit, color: 'bg-amber-100 text-amber-700' },
  { value: 'qc_operations', label: 'QC Operations', icon: FileEdit, color: 'bg-amber-100 text-amber-700' },
  { value: 'inhouse_verification', label: 'In-house Verification Team', icon: Home, color: 'bg-blue-100 text-blue-700' },
  { value: 'verification_team', label: 'Verification Team (General)', icon: User, color: 'bg-slate-100 text-slate-700' },
  { value: 'vendor_management', label: 'Vendor Management Team', icon: Truck, color: 'bg-purple-100 text-purple-700' },
  { value: 'operations_manager', label: 'Operations Manager', icon: Briefcase, color: 'bg-green-100 text-green-700' },
];

// Job Title / Display Roles for team members
const JOB_TITLES = [
  { value: 'qc_manager', label: 'QC Manager' },
  { value: 'operations_manager_vendor', label: 'Operations Manager - Vendor Team' },
  { value: 'operations_manager_inhouse', label: 'Operations Manager - Inhouse Team' },
  { value: 'agent', label: 'Agent' },
  { value: 'cre_manager', label: 'CRE Manager' },
  { value: 'cre_executive', label: 'CRE Executive' },
  { value: 'team_lead', label: 'Team Lead' },
  { value: 'senior_executive', label: 'Senior Executive' },
  { value: 'executive', label: 'Executive' },
  { value: 'trainee', label: 'Trainee' },
];

const CHECK_TYPES = [
  'Address Verification', 'Employment Verification', 'Education Verification',
  'ID Verification', 'Criminal Check', 'Criminal Advocate Check', 'Credit Check', 
  'Reference Check', 'Drug Test', 'Global Database'
];

export default function TeamTab() {
  const [teamMembers, setTeamMembers] = useState([]);
  const [clients, setClients] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddMember, setShowAddMember] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [activeTeamTab, setActiveTeamTab] = useState('all');

  const [newMember, setNewMember] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    role: 'verification_team',
    job_title: '',  // Display role like QC Manager, Agent, etc.
    assigned_client_ids: [],
    assigned_check_types: [],
    max_concurrent_checks: 10
  });
  
  const [editMember, setEditMember] = useState({
    name: '',
    email: '',
    phone: '',
    role: 'verification_team',
    job_title: ''
  });
  const [entities, setEntities] = useState([]);

  const [assignmentData, setAssignmentData] = useState({
    team_type: '',
    assigned_check_types: [],
    assigned_client_ids: [],
    max_concurrent_checks: 10
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [membersRes, clientsRes, assignmentsRes, entitiesRes] = await Promise.all([
        axios.get('/team/members'),
        axios.get('/clients'),
        axios.get('/agent-assignments'),
        axios.get('/entities')
      ]);
      setTeamMembers(membersRes.data);
      setClients(clientsRes.data);
      setAssignments(assignmentsRes.data);
      setEntities(entitiesRes.data || []);
    } catch (error) {
      toast.error('Failed to load team data');
    } finally {
      setLoading(false);
    }
  };

  // Toggle member active/inactive status
  const toggleMemberStatus = async (member) => {
    const newStatus = !member.is_active;
    const action = newStatus ? 'activate' : 'deactivate';
    
    if (!newStatus && !window.confirm(`Are you sure you want to deactivate ${member.name}? They will not be able to access the system and their assigned checks will need to be reassigned.`)) {
      return;
    }
    
    try {
      await axios.patch(`/users/${member.id}/status`, { is_active: newStatus });
      toast.success(`${member.name} has been ${action}d successfully`);
      fetchData();
    } catch (error) {
      toast.error(`Failed to ${action} team member`);
    }
  };

  const handleAddMember = async (e) => {
    e.preventDefault();
    try {
      // First register the user
      const registerRes = await axios.post('/auth/register', {
        name: newMember.name,
        email: newMember.email,
        phone: newMember.phone,
        password: newMember.password,
        role: newMember.role
      });
      
      // If assignment options were selected, save the assignment
      if (newMember.assigned_client_ids.length > 0 || newMember.assigned_check_types.length > 0) {
        const userId = registerRes.data?.user?.id || registerRes.data?.id;
        if (userId) {
          await axios.post('/agent-assignments', {
            user_id: userId,
            team_type: newMember.role,
            assigned_check_types: newMember.assigned_check_types,
            assigned_client_ids: newMember.assigned_client_ids,
            max_concurrent_checks: newMember.max_concurrent_checks
          });
        }
      }
      
      toast.success('Team member added successfully');
      setShowAddMember(false);
      setNewMember({ 
        name: '', email: '', phone: '', password: '', role: 'verification_team', job_title: '',
        assigned_client_ids: [], assigned_check_types: [], max_concurrent_checks: 10
      });
      fetchData();
    } catch (error) {
      let errorMsg = error.response?.data?.detail || 'Failed to add team member';
      // Ensure errorMsg is a string (API might return object in some cases)
      if (typeof errorMsg !== 'string') {
        errorMsg = JSON.stringify(errorMsg) || 'Failed to add team member';
      }
      if (errorMsg.toLowerCase().includes('email already registered') || errorMsg.toLowerCase().includes('already exists')) {
        toast.error('This email is already registered. Use the "Configure" button to update an existing team member\'s assignments.');
      } else {
        toast.error(errorMsg);
      }
    }
  };

  const openAssignModal = (member) => {
    setSelectedMember(member);
    const existingAssignment = member.assignment || {};
    setAssignmentData({
      team_type: existingAssignment.team_type || member.role || '',
      assigned_check_types: existingAssignment.assigned_check_types || [],
      assigned_client_ids: existingAssignment.assigned_client_ids || [],
      max_concurrent_checks: existingAssignment.max_concurrent_checks || 10
    });
    setShowAssignModal(true);
  };

  const handleSaveAssignment = async () => {
    if (!selectedMember) return;
    try {
      await axios.post('/agent-assignments', {
        user_id: selectedMember.id,
        ...assignmentData
      });
      toast.success('Assignment saved successfully');
      setShowAssignModal(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to save assignment');
    }
  };

  const openEditModal = (member) => {
    setSelectedMember(member);
    setEditMember({
      name: member.name || '',
      email: member.email || '',
      phone: member.phone || '',
      role: member.role || 'verification_team',
      job_title: member.job_title || ''
    });
    setShowEditModal(true);
  };

  const handleEditMember = async (e) => {
    e.preventDefault();
    if (!selectedMember) return;
    
    try {
      await axios.put(`/users/${selectedMember.id}`, {
        name: editMember.name,
        email: editMember.email,
        phone: editMember.phone,
        role: editMember.role,
        job_title: editMember.job_title
      });
      toast.success('Team member updated successfully');
      setShowEditModal(false);
      setSelectedMember(null);
      fetchData();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to update team member';
      toast.error(typeof errorMsg === 'string' ? errorMsg : 'Failed to update team member');
    }
  };

  const getRoleBadge = (role, assignment = null) => {
    // Use team_type from assignment if available, otherwise fall back to role
    const teamType = assignment?.team_type || role;
    const roleConfig = TEAM_ROLES.find(r => r.value === teamType);
    if (!roleConfig) return <Badge variant="outline">{teamType?.replace(/_/g, ' ')}</Badge>;
    return <Badge className={roleConfig.color}>{roleConfig.label}</Badge>;
  };

  const getFilteredMembers = () => {
    if (activeTeamTab === 'all') return teamMembers;
    
    const roleMap = {
      'data_entry': ['data_entry_team', 'qc_operations'],
      'inhouse': ['inhouse_verification', 'verification_team'],
      'vendor': ['vendor_management']
    };
    
    return teamMembers.filter(m => roleMap[activeTeamTab]?.includes(m.role));
  };

  if (loading) {
    return <div className="p-8 text-center">Loading...</div>;
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-4xl font-bold text-slate-900 mb-2">Verification Teams</h1>
          <p className="text-slate-600">Manage team members and their assignments</p>
        </div>
        <Button onClick={() => setShowAddMember(true)} className="bg-slate-900 hover:bg-slate-800" data-testid="add-team-member-button">
          <Plus className="w-4 h-4 mr-2" />
          Add Team Member
        </Button>
      </div>

      {/* Team Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card className="border-slate-200 shadow-sm cursor-pointer hover:shadow-md" onClick={() => setActiveTeamTab('all')}>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total Members</p>
                <p className="text-2xl font-bold">{teamMembers.length}</p>
              </div>
              <Users className="w-8 h-8 text-slate-400" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-amber-200 bg-amber-50 shadow-sm cursor-pointer hover:shadow-md" onClick={() => setActiveTeamTab('data_entry')}>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-amber-700">Data Entry / QC</p>
                <p className="text-2xl font-bold text-amber-900">
                  {teamMembers.filter(m => ['data_entry_team', 'qc_operations'].includes(m.role)).length}
                </p>
              </div>
              <FileEdit className="w-8 h-8 text-amber-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-blue-200 bg-blue-50 shadow-sm cursor-pointer hover:shadow-md" onClick={() => setActiveTeamTab('inhouse')}>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-blue-700">In-house Team</p>
                <p className="text-2xl font-bold text-blue-900">
                  {teamMembers.filter(m => ['inhouse_verification', 'verification_team'].includes(m.role)).length}
                </p>
              </div>
              <Home className="w-8 h-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-purple-200 bg-purple-50 shadow-sm cursor-pointer hover:shadow-md" onClick={() => setActiveTeamTab('vendor')}>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-purple-700">Vendor Management</p>
                <p className="text-2xl font-bold text-purple-900">
                  {teamMembers.filter(m => m.role === 'vendor_management').length}
                </p>
              </div>
              <Truck className="w-8 h-8 text-purple-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Team Filter Tabs */}
      <Tabs value={activeTeamTab} onValueChange={setActiveTeamTab} className="mb-6">
        <TabsList>
          <TabsTrigger value="all">All Teams</TabsTrigger>
          <TabsTrigger value="data_entry">Data Entry / QC</TabsTrigger>
          <TabsTrigger value="inhouse">In-house Verification</TabsTrigger>
          <TabsTrigger value="vendor">Vendor Management</TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Team Members Table */}
      {getFilteredMembers().length > 0 ? (
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Team</TableHead>
                  <TableHead>Assigned Checks</TableHead>
                  <TableHead>Assigned Clients</TableHead>
                  <TableHead>Workload</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {getFilteredMembers().map((member) => (
                  <TableRow key={member.id} data-testid={`team-member-row-${member.id}`}>
                    <TableCell className="font-medium">{member.name}</TableCell>
                    <TableCell>
                      <div>
                        <p className="text-sm">{member.email}</p>
                        {member.job_title && (
                          <Badge className="mt-1 bg-indigo-100 text-indigo-700 text-xs">
                            {JOB_TITLES.find(t => t.value === member.job_title)?.label || member.job_title}
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>{getRoleBadge(member.role, member.assignment)}</TableCell>
                    <TableCell>
                      <div className="flex gap-1 flex-wrap max-w-[200px]">
                        {(() => {
                          // Use assigned_check_types from assignment or directly from user
                          const checkTypes = member.assignment?.assigned_check_types || member.assigned_check_types || [];
                          return (
                            <>
                              {checkTypes.slice(0, 2).map((type, i) => (
                                <Badge key={i} variant="outline" className="text-xs">{type.replace('_check', '').replace('_', ' ')}</Badge>
                              ))}
                              {checkTypes.length > 2 && (
                                <Badge variant="outline" className="text-xs">+{checkTypes.length - 2}</Badge>
                              )}
                              {checkTypes.length === 0 && (
                                <span className="text-slate-400 text-sm">All</span>
                              )}
                            </>
                          );
                        })()}
                      </div>
                    </TableCell>
                    <TableCell>
                      {member.assignment?.assigned_client_ids?.length > 0 ? (
                        <Badge variant="outline">{member.assignment.assigned_client_ids.length} clients</Badge>
                      ) : (
                        <span className="text-slate-400 text-sm">All</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <span className={member.assignment?.current_workload > 5 ? 'text-amber-600 font-medium' : ''}>
                        {member.assignment?.current_workload || 0} / {member.assignment?.max_concurrent_checks || 10}
                      </span>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Badge className={member.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}>
                          {member.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => toggleMemberStatus(member)}
                          className={member.is_active ? 'text-red-600 hover:bg-red-50' : 'text-emerald-600 hover:bg-emerald-50'}
                          title={member.is_active ? 'Deactivate member' : 'Activate member'}
                        >
                          {member.is_active ? <XCircle className="w-4 h-4" /> : <CheckCircle className="w-4 h-4" />}
                        </Button>
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex gap-2 justify-end">
                        <Button variant="outline" size="sm" onClick={() => openEditModal(member)} title="Edit member details">
                          <Edit className="w-4 h-4 mr-1" />
                          Edit
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => openAssignModal(member)} title="Configure assignments">
                          <Settings className="w-4 h-4 mr-1" />
                          Configure
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      ) : (
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="text-center py-12">
            <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-600 mb-4">No team members in this category</p>
            <Button onClick={() => setShowAddMember(true)} className="bg-slate-900 hover:bg-slate-800">
              <Plus className="w-4 h-4 mr-2" />
              Add Team Member
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Add Member Dialog */}
      <Dialog open={showAddMember} onOpenChange={setShowAddMember}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add Team Member</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddMember}>
            <div className="grid grid-cols-2 gap-4">
              {/* Basic Info Section */}
              <div className="space-y-4">
                <p className="text-sm font-medium text-slate-700 border-b pb-2">Basic Information</p>
                <div>
                  <Label>Name *</Label>
                  <Input
                    value={newMember.name}
                    onChange={(e) => setNewMember({ ...newMember, name: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label>Email *</Label>
                  <Input
                    type="email"
                    value={newMember.email}
                    onChange={(e) => setNewMember({ ...newMember, email: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label>Phone</Label>
                  <Input
                    value={newMember.phone}
                    onChange={(e) => setNewMember({ ...newMember, phone: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Password *</Label>
                  <Input
                    type="password"
                    value={newMember.password}
                    onChange={(e) => setNewMember({ ...newMember, password: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label>Team Role *</Label>
                  <Select value={newMember.role} onValueChange={(v) => setNewMember({ ...newMember, role: v })}>
                    <SelectTrigger><SelectValue placeholder="Select role" /></SelectTrigger>
                    <SelectContent>
                      {TEAM_ROLES.map((role) => (
                        <SelectItem key={role.value} value={role.value}>{role.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Job Title / Role *</Label>
                  <Select value={newMember.job_title} onValueChange={(v) => setNewMember({ ...newMember, job_title: v })}>
                    <SelectTrigger><SelectValue placeholder="Select job title" /></SelectTrigger>
                    <SelectContent>
                      {JOB_TITLES.map((title) => (
                        <SelectItem key={title.value} value={title.value}>{title.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-500 mt-1">This will be displayed on dashboard</p>
                </div>
              </div>

              {/* Assignment Section */}
              <div className="space-y-4">
                <p className="text-sm font-medium text-slate-700 border-b pb-2">Assignment (Optional)</p>
                
                <div>
                  <Label>Assigned Clients</Label>
                  <p className="text-xs text-slate-500 mb-2">Leave empty for all clients</p>
                  <div className="max-h-32 overflow-y-auto border rounded-lg p-2">
                    {clients.length === 0 ? (
                      <p className="text-xs text-slate-400 py-2">No clients available</p>
                    ) : (
                      clients.map((client) => (
                        <label key={client.id} className="flex items-center gap-2 text-sm cursor-pointer py-1">
                          <input
                            type="checkbox"
                            checked={newMember.assigned_client_ids.includes(client.id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setNewMember({...newMember, assigned_client_ids: [...newMember.assigned_client_ids, client.id]});
                              } else {
                                setNewMember({...newMember, assigned_client_ids: newMember.assigned_client_ids.filter(id => id !== client.id)});
                              }
                            }}
                            className="rounded"
                          />
                          {client.company_name}
                        </label>
                      ))
                    )}
                  </div>
                </div>

                <div>
                  <Label>Assigned Check Types</Label>
                  <p className="text-xs text-slate-500 mb-2">Leave empty for all check types</p>
                  <div className="grid grid-cols-2 gap-1 max-h-40 overflow-y-auto border rounded-lg p-2">
                    {CHECK_TYPES.map((type) => (
                      <label key={type} className="flex items-center gap-2 text-xs cursor-pointer py-1">
                        <input
                          type="checkbox"
                          checked={newMember.assigned_check_types.includes(type)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setNewMember({...newMember, assigned_check_types: [...newMember.assigned_check_types, type]});
                            } else {
                              setNewMember({...newMember, assigned_check_types: newMember.assigned_check_types.filter(t => t !== type)});
                            }
                          }}
                          className="rounded"
                        />
                        {type.replace(' Verification', '').replace(' Check', '')}
                      </label>
                    ))}
                  </div>
                </div>

                <div>
                  <Label>Max Concurrent Checks</Label>
                  <Input
                    type="number"
                    value={newMember.max_concurrent_checks}
                    onChange={(e) => setNewMember({...newMember, max_concurrent_checks: parseInt(e.target.value) || 10})}
                    min={1}
                    max={50}
                  />
                </div>
              </div>
            </div>
            <DialogFooter className="mt-6">
              <Button type="button" variant="outline" onClick={() => setShowAddMember(false)}>Cancel</Button>
              <Button type="submit" className="bg-slate-900 hover:bg-slate-800">Add Member</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Configure Assignment Dialog */}
      <Dialog open={showAssignModal} onOpenChange={setShowAssignModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Configure Team Member</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {selectedMember && (
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="font-medium">{selectedMember.name}</p>
                <p className="text-sm text-slate-500">{selectedMember.email}</p>
              </div>
            )}

            <div>
              <Label>Team Type</Label>
              <Select value={assignmentData.team_type} onValueChange={(v) => setAssignmentData({...assignmentData, team_type: v})}>
                <SelectTrigger><SelectValue placeholder="Select team type" /></SelectTrigger>
                <SelectContent>
                  {TEAM_ROLES.map((role) => (
                    <SelectItem key={role.value} value={role.value}>{role.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Assigned Check Types</Label>
              <p className="text-xs text-slate-500 mb-2">Leave empty for all check types</p>
              <div className="grid grid-cols-2 gap-2">
                {CHECK_TYPES.map((type) => (
                  <label key={type} className="flex items-center gap-2 text-sm cursor-pointer">
                    <input
                      type="checkbox"
                      checked={assignmentData.assigned_check_types.includes(type)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setAssignmentData({...assignmentData, assigned_check_types: [...assignmentData.assigned_check_types, type]});
                        } else {
                          setAssignmentData({...assignmentData, assigned_check_types: assignmentData.assigned_check_types.filter(t => t !== type)});
                        }
                      }}
                      className="rounded"
                    />
                    {type.replace(' Verification', '').replace(' Check', '')}
                  </label>
                ))}
              </div>
            </div>

            <div>
              <Label>Assigned Clients</Label>
              <p className="text-xs text-slate-500 mb-2">Leave empty for all clients</p>
              <div className="max-h-40 overflow-y-auto border rounded-lg p-2">
                {clients.map((client) => (
                  <label key={client.id} className="flex items-center gap-2 text-sm cursor-pointer py-1">
                    <input
                      type="checkbox"
                      checked={assignmentData.assigned_client_ids.includes(client.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setAssignmentData({...assignmentData, assigned_client_ids: [...assignmentData.assigned_client_ids, client.id]});
                        } else {
                          setAssignmentData({...assignmentData, assigned_client_ids: assignmentData.assigned_client_ids.filter(id => id !== client.id)});
                        }
                      }}
                      className="rounded"
                    />
                    {client.company_name}
                  </label>
                ))}
              </div>
            </div>

            <div>
              <Label>Max Concurrent Checks</Label>
              <Input
                type="number"
                value={assignmentData.max_concurrent_checks}
                onChange={(e) => setAssignmentData({...assignmentData, max_concurrent_checks: parseInt(e.target.value) || 10})}
                min={1}
                max={50}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAssignModal(false)}>Cancel</Button>
            <Button onClick={handleSaveAssignment} className="bg-slate-900 hover:bg-slate-800">Save Assignment</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Member Dialog */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Team Member</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEditMember}>
            <div className="space-y-4">
              <div>
                <Label>Name *</Label>
                <Input
                  value={editMember.name}
                  onChange={(e) => setEditMember({ ...editMember, name: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label>Email *</Label>
                <Input
                  type="email"
                  value={editMember.email}
                  onChange={(e) => setEditMember({ ...editMember, email: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label>Phone</Label>
                <Input
                  value={editMember.phone}
                  onChange={(e) => setEditMember({ ...editMember, phone: e.target.value })}
                />
              </div>
              <div>
                <Label>Team Role *</Label>
                <Select value={editMember.role} onValueChange={(v) => setEditMember({ ...editMember, role: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TEAM_ROLES.map(role => (
                      <SelectItem key={role.value} value={role.value}>{role.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Job Title</Label>
                <Select value={editMember.job_title || 'none'} onValueChange={(v) => setEditMember({ ...editMember, job_title: v === 'none' ? '' : v })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select job title" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {JOB_TITLES.map(title => (
                      <SelectItem key={title.value} value={title.value}>{title.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter className="mt-6">
              <Button type="button" variant="outline" onClick={() => setShowEditModal(false)}>Cancel</Button>
              <Button type="submit" className="bg-slate-900 hover:bg-slate-800">Save Changes</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
